import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, TrendingUp, Target, PenLine, Zap, CheckCircle, XCircle } from 'lucide-react';
import { NBA_TEAM_IDS } from '@/lib/nba-teams';
import { useAuth } from '@/context/AuthContext';
import { UserAvatar } from '@/components/UserAvatar';
import { exactScorePoints } from '@/lib/scoring';
import type { UserSideBetPick } from '@/lib/supabase-queries';
import type { GlobalSideBet } from '@/types/side-bets';
import { getSideBetStatusBadge, isSideBetCorrect } from '@/lib/side-bets-display';

interface MyBetsProps {
  bets: any[];
  seriesList: any[];
  rank: number | null;
  leaderboardTotal?: number | null;
  isViewingOther?: boolean;
  targetDisplayName?: string | null;
  targetAvatarId?: number | null;
  sideBetPicks?: UserSideBetPick[];
  allSideBets?: GlobalSideBet[];
}

function getBetStatus(bet: any, series: any) {
  if (!series) return { label: 'ממתין', classes: 'bg-secondary text-muted-foreground' };
  if (series.status === 'in_progress') {
    return {
      label: `בתהליך ${series.home_wins || 0}-${series.away_wins || 0}`,
      classes: 'bg-blue-500/20 text-blue-400',
    };
  }
  if (series.status !== 'completed') {
    return { label: 'ממתין', classes: 'bg-secondary text-muted-foreground' };
  }
  if (bet.is_correct_score) {
    return { label: '✅ תוצאה מדויקת!', classes: 'bg-green-500/20 text-green-400' };
  }
  if (bet.is_correct_winner) {
    return { label: '✅ מנצחת נכונה', classes: 'bg-green-500/10 text-green-300' };
  }
  return { label: '❌ בפעם הבאה', classes: 'bg-red-500/10 text-red-400/70' };
}

interface BetCardProps {
  bet: any;
  series: any;
  status: { label: string; classes: string };
  showBigPoints: boolean;
  logoUrl: string | null;
  index: number;
}

function BetCard({ bet, series, status, showBigPoints, logoUrl, index }: BetCardProps) {
  const imgRef = useRef<HTMLImageElement>(null);

  function handleLogoError() {
    if (imgRef.current) {
      imgRef.current.style.display = 'none';
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index + 1) * 0.05 }}
      className="relative overflow-hidden bg-card border border-border rounded-2xl p-5"
    >
      {/* Team logo background */}
      {logoUrl && (
        <img
          ref={imgRef}
          src={logoUrl}
          alt=""
          aria-hidden="true"
          onError={handleLogoError}
          className="absolute top-1/2 -translate-y-1/2 right-3 h-[80%] aspect-square object-contain opacity-[0.12] pointer-events-none select-none z-[1] md:right-4 md:opacity-[0.18]"
        />
      )}

      {/* Gradient overlay for text readability over watermark */}
      {logoUrl && (
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-card/40 to-card/80 z-[2] pointer-events-none" />
      )}

      {/* Card content — above background */}
      <div className="relative z-[3]">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold">
            {series?.team_home || '?'} vs {series?.team_away || '?'}
          </span>
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${status.classes}`}>
            {status.label}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <TrendingUp className="w-3.5 h-3.5" />
          <span>ניחוש: {bet.predicted_score}</span>
          {series?.status !== 'completed' && (
            <span className="text-accent font-semibold text-xs tabular-nums flex items-center gap-1">
              <Target className="w-3 h-3" />
              {exactScorePoints(bet.odds_at_bet ?? null)} נק' בניחוש נכון
            </span>
          )}
          {bet.is_commissioner_edited && (
            <span className="inline-flex items-center gap-1 text-amber-400 text-xs" title="נערך ע״י הקומישינר">
              <PenLine className="w-3 h-3" />
              נערך
            </span>
          )}
        </div>

        {showBigPoints && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: [1, 1.15, 0.95, 1.05, 1], opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex items-center gap-2 mt-3 pt-3 border-t border-border"
            style={{ filter: 'drop-shadow(0 0 8px hsl(120 60% 50% / 0.4))' }}
          >
            <Trophy className="w-4 h-4 text-accent" />
            <span className="text-xl font-bold text-accent tabular-nums">+{bet.points_earned}</span>
            <span className="text-xs text-muted-foreground">נקודות</span>
          </motion.div>
        )}

        {bet.is_correct_winner && !bet.is_correct_score && (bet.points_earned || 0) > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
            <Trophy className="w-4 h-4 text-green-400" />
            <span className="text-lg font-bold text-green-400 tabular-nums">+{bet.points_earned}</span>
            <span className="text-xs text-muted-foreground">נקודות</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

const MyBets = ({
  bets,
  seriesList,
  rank,
  leaderboardTotal,
  isViewingOther,
  targetDisplayName,
  targetAvatarId,
  sideBetPicks = [],
  allSideBets = [],
}: MyBetsProps) => {
  const { avatarId, displayName } = useAuth();
  const { groupId } = useParams<{ groupId: string }>();

  const shownAvatarId = isViewingOther ? (targetAvatarId ?? null) : avatarId;
  const shownName = isViewingOther ? (targetDisplayName ?? '') : displayName;

  // ── Derived data ─────────────────────────────────────
  const bettedSeriesIds = new Set(bets.map(b => b.series_id));
  const unbettedSeries = seriesList.filter(
    s => !bettedSeriesIds.has(s.id) && (s.status === 'not_started' || s.status === 'in_progress')
  );

  const pickedSideBetIds = new Set(sideBetPicks.map(p => p.side_bet_id));
  const unpickedOpenSideBets = allSideBets.filter(
    sb => sb.status === 'open' && !pickedSideBetIds.has(sb.id)
  );

  // Total activity: placed series bets + placed side bet picks
  const totalActivities = bets.length + sideBetPicks.length;

  // Completed / resolved accuracy across BOTH bet types
  const completedBets = bets.filter(b => {
    const s = seriesList.find(sr => sr.id === b.series_id);
    return s?.status === 'completed';
  });
  const correctWinners = completedBets.filter(b => b.is_correct_winner).length;
  const correctScores = completedBets.filter(b => b.is_correct_score).length;

  const resolvedPicks = sideBetPicks.filter(p => p.status === 'resolved');
  const correctSideBets = resolvedPicks.filter(isSideBetCorrect).length;

  const totalCompleted = completedBets.length + resolvedPicks.length;
  const totalCorrect = correctWinners + correctSideBets;

  // Prefer the authoritative leaderboard total (includes side bet + champion bonus);
  // fall back to summing series bet points if the leaderboard isn't available.
  const seriesPoints = bets.reduce((sum, b) => sum + (b.points_earned || 0), 0);
  const sideBetPoints = sideBetPicks.reduce((sum, p) => sum + (p.points_earned || 0), 0);
  const totalPoints =
    typeof leaderboardTotal === 'number' ? leaderboardTotal : seriesPoints + sideBetPoints;

  // Sort side bet picks by round, then by pick creation time for stable order
  const sortedPicks = [...sideBetPicks].sort((a, b) => {
    if (a.round_number !== b.round_number) return a.round_number - b.round_number;
    return a.created_at.localeCompare(b.created_at);
  });

  // ── Empty state (nothing placed and nothing open to place) ─────
  const hasAnyContent =
    totalActivities > 0 || unbettedSeries.length > 0 || unpickedOpenSideBets.length > 0;

  if (!hasAnyContent) {
    return (
      <div className="text-center py-20">
        <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
        <p className="text-muted-foreground font-medium">
          {isViewingOther ? 'אין הימורים גלויים עדיין' : 'עדיין לא ניחשת! 🎯'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {isViewingOther ? 'הימורים ייחשפו אחרי שהסדרה מתחילה' : 'לחץ על סדרה פתוחה כדי להתחיל'}
        </p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Summary card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-2xl p-6"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <UserAvatar avatarId={shownAvatarId} displayName={shownName} size="md" />
            <span className="font-semibold">{shownName}</span>
          </div>
          {rank !== null && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <span>מקום</span>
              <span className="font-bold text-foreground">#{rank}</span>
            </div>
          )}
        </div>
        <p className="section-label mb-2">סה״כ נקודות</p>
        <p className="text-3xl md:text-4xl font-bold text-accent tabular-nums">{totalPoints}</p>
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border">
          <div>
            <p className="text-lg font-bold tabular-nums">{totalActivities}</p>
            <p className="text-xs text-muted-foreground">הימורים</p>
          </div>
          <div>
            <p className="text-lg font-bold tabular-nums">
              {totalCompleted > 0 ? `${totalCorrect}/${totalCompleted}` : '—'}
            </p>
            <p className="text-xs text-muted-foreground">כיוון נכון</p>
          </div>
          <div>
            <p className="text-lg font-bold tabular-nums">{correctScores}</p>
            <p className="text-xs text-muted-foreground">תוצאות מדויקות</p>
          </div>
        </div>
      </motion.div>

      {/* ── Series section ───────────────────────── */}
      {(bets.length > 0 || unbettedSeries.length > 0) && (
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-accent" />
            <h3 className="text-sm font-medium uppercase tracking-[0.15em] text-accent">הימורי סדרות</h3>
          </div>

          {bets.map((bet, i) => {
            const series = seriesList.find(sr => sr.id === bet.series_id);
            const status = getBetStatus(bet, series);
            const showBigPoints = bet.is_correct_score && (bet.points_earned || 0) > 0;
            const teamId = NBA_TEAM_IDS[bet.predicted_winner];
            const logoUrl = teamId
              ? `https://cdn.nba.com/logos/nba/${teamId}/primary/L/logo.svg`
              : null;

            return (
              <div key={bet.id} className="mb-4">
                <BetCard
                  bet={bet}
                  series={series}
                  status={status}
                  showBigPoints={showBigPoints}
                  logoUrl={logoUrl}
                  index={i}
                />
              </div>
            );
          })}

          {/* Unbetted open series — "not yet bet" CTA (own view only) */}
          {!isViewingOther && unbettedSeries.map((series, i) => (
            <motion.div
              key={series.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (bets.length + i + 1) * 0.05 }}
              className="border-2 border-dashed border-accent/60 rounded-2xl p-5 mb-4"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">
                  {series.team_home} vs {series.team_away}
                </span>
                <span className="text-xs px-3 py-1 rounded-full font-medium bg-accent/10 text-accent">
                  ממתין להימור
                </span>
              </div>
              <Link
                to={`/group/${groupId}/series/${series.id}`}
                className="inline-block text-sm font-medium text-accent hover:text-accent/80 transition-colors"
              >
                לחץ להמר ←
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── Side bets section ────────────────────── */}
      {(sortedPicks.length > 0 || (!isViewingOther && unpickedOpenSideBets.length > 0)) && (
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#C4A467]" />
            <h3 className="text-sm font-medium uppercase tracking-[0.15em] text-[#C4A467]">הימורי צד</h3>
          </div>

          {sortedPicks.map((pick, i) => {
            const displayOption = pick.selected_option === 'other' && pick.other_text
              ? `אחר: ${pick.other_text}`
              : pick.selected_option;
            const isCorrect = isSideBetCorrect(pick);
            const isResolved = pick.status === 'resolved';
            const badge = getSideBetStatusBadge(pick.status);
            const maxPts = pick.selected_option === 'other' && pick.points_other > 0
              ? pick.points_other
              : pick.points_hit;

            const borderStyle = isResolved
              ? isCorrect
                ? { borderColor: 'rgba(34,197,94,0.3)' }
                : { borderColor: 'rgba(239,68,68,0.3)' }
              : undefined;

            return (
              <motion.div
                key={pick.pick_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i + 1) * 0.05 }}
                className="bg-card border border-border rounded-2xl p-4 mb-3"
                style={borderStyle}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-sm font-medium flex-1 text-right">{pick.title_he}</p>

                  {isResolved ? (
                    isCorrect ? (
                      <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                    )
                  ) : (
                    <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full ${badge.classes}`}>
                      {badge.label}
                    </span>
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-right">
                  הבחירה שלך: <span className="text-foreground font-medium">{displayOption}</span>
                </p>

                {/* Fixed-value hint for non-resolved picks (side bets are hit-or-miss, not a range) */}
                {!isResolved && maxPts > 0 && (
                  <p className="text-xs text-accent/80 text-right mt-1 tabular-nums flex items-center gap-1 justify-end">
                    <Target className="w-3 h-3" />
                    <span>{maxPts} נק' בניחוש נכון</span>
                  </p>
                )}

                {/* Edit affordance for open picks (own view only) */}
                {pick.status === 'open' && !isViewingOther && (
                  <div className="mt-2 pt-2 border-t border-border/40 text-right">
                    <Link
                      to={`/group/${groupId}/side-bets`}
                      className="text-xs text-accent hover:underline"
                    >
                      ערוך הימור ←
                    </Link>
                  </div>
                )}

                {isResolved && pick.correct_answer && !isCorrect && (
                  <p className="text-xs text-green-400/70 text-right mt-1">
                    תשובה נכונה: {pick.correct_answer}
                  </p>
                )}

                {pick.points_earned > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/40">
                    <Trophy className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-sm font-bold text-green-400 tabular-nums">+{pick.points_earned}</span>
                    <span className="text-xs text-muted-foreground">נק׳</span>
                  </div>
                )}
              </motion.div>
            );
          })}

          {/* Unpicked open side bets — "not yet picked" CTA (own view only) */}
          {!isViewingOther && unpickedOpenSideBets.map((sb, i) => (
            <motion.div
              key={sb.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (sortedPicks.length + i + 1) * 0.05 }}
              className="border-2 border-dashed rounded-2xl p-5 mb-3"
              style={{ borderColor: 'rgba(196,164,103,0.45)' }}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className="font-medium text-sm flex-1 text-right">{sb.title_he}</span>
                <span
                  className="shrink-0 text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(196,164,103,0.15)', color: '#C4A467' }}
                >
                  ממתין להימור
                </span>
              </div>
              <Link
                to={`/group/${groupId}/side-bets`}
                className="inline-block text-sm font-medium hover:opacity-80 transition-opacity"
                style={{ color: '#C4A467' }}
              >
                לחץ להמר ←
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBets;
